// One derivation for the whole screen: the rail's rows, the marks the wheel
// draws, and the marks „Warstwy" counts — from one snapshot, one view, one
// centre and one clock.
//
// Pure, like everything in `derive/`: no browser global, no `Date.now()`. The
// clock is an input and rides back out on the result, so a consumer asserts
// `derived.now` instead of reading the clock a second time.
//
// **One object in, one object out**, deliberately. Decision 47 (#238) added
// `order` to the input — it is `view.order`, because the order is a key of the
// address like every other view field (§2.3) — and `placed`, `placeless`,
// `beyond` and `summary` to the result. A `remeasure(prev, centre, radius)`
// sibling for the live half of a gesture is V4's, and none of it reshapes what
// is here.
import type { Snapshot } from '../data/data.ts';
import type { AxisRow, Item } from '../shared/model.ts';
import type { ViewState } from '../state/viewstate.ts';
import { buildAxis } from './axis.ts';

export type DeriveInput = {
  snapshot: Snapshot;
  view: ViewState;
  /** The centre distances are measured from. Never a singleton: a gesture moves it. */
  centre: { lat: number; lon: number };
  /** The ONE clock this derivation reads by — `Date.now()` in the caller. */
  now: number;
};

export type Derived = {
  /** The rail, in time order. Mute runs, notes, the gap band and the tick included. */
  rows: AxisRow[];
  /**
   * What the wheel draws, in axis order.
   *
   * A mark is gated on exactly three things, and it is worth naming them
   * together because no two surfaces may disagree about them (D-4):
   *
   *   1. the mixer — a muted voice, or any voice outside `solo`, draws nothing;
   *   2. near-now — an event more than 3 h ahead or 2 h past earns no mark,
   *      unless it is the open row;
   *   3. the radius — an item further than `view.radius`, or with no position
   *      at all, keeps its row and is simply not drawn.
   *
   * Two things it is **not** gated on, and they do not have the same standing:
   *
   * `view.onlyLine` is intended. The line lens collapses rows on the axis but
   * only DIMS marks on the wheel (SPEC-redesign D-q), so the marks outside the
   * lens have to survive the derivation for the view to fade them.
   *
   * `view.lines` is unstated. The rail lists the resident's own lines and
   * nobody else's, but every vehicle inside the radius gets a mark whatever
   * line it runs — so the circle carries vehicles the rail has no row for. That
   * is today's behaviour, it is not written down anywhere as a decision, and
   * #217 is the issue that will settle it. Until then this comment is the only
   * record that the asymmetry is known rather than accidental.
   */
  marked: Item[];
  /**
   * `marked` out to 1,6 × the radius — the marks the live half of a gesture may
   * need to SHOW as the ring passes over them (§4.1, point 3). Nothing draws it
   * at rest, and `src/derive/remeasure.ts` is its only reader.
   */
  nearby: Item[];
  /**
   * The same set with the silences lifted — what „Warstwy" counts (#163).
   *
   * This is why one derivation can serve all three callers at all: the mixer
   * needs the unsilenced set at the same instant the wheel needs the silenced
   * one, and they are two results from one snapshot, not one result read twice.
   */
  markedUnsilenced: Item[];
  /**
   * What the ring holds, what it leaves out and what has no place at all — the
   * three sets `buildAxis` divides the rail's own items into (§2.1, §2.2).
   * `placed.length` is the toolbar's „w promieniu N", `beyond.length` its
   * „poza M", and `beyond` is what V4 draws as dots (§3).
   */
  placed: Item[];
  placeless: Item[];
  beyond: Item[];
  /**
   * What the kreska's „N pojazdów" counts, and the whole of §2.5: the vehicles
   * inside the radius, with the mixer's silences lifted.
   *
   * The universe and not the answer. The kreska goes on deciding which of them
   * are the resident's, what the line lens does and how the Polish counts —
   * what changes is that it is handed the vehicles in REACH instead of
   * counting a snapshot of its own, which is what made „Linie 16, 174, 2
   * pojazdy" and a circle showing one both true at once.
   *
   * Taken from `markedUnsilenced`, which is what „Warstwy" counts, so the two
   * agree by construction rather than by two filters kept in step by hope.
   */
  summary: { vehicles: Item[] };
  /** The clock this result was built for, so a consumer cannot mix two. */
  now: number;
};

/**
 * The view the ZTM row's count asks about: this one, with its silences lifted.
 *
 * A row in the mixer describes **what its switch would bring back**, not what is
 * on the wheel at this instant. Counting from the view as it stands makes
 * „Pojazdy ZTM" read „0 pojazdów moich linii" the moment it is switched off —
 * an answer to a question nobody asked, and the one thing that makes the switch
 * impossible to find your way back through. Measured in the prototype: the count
 * is the same with the switch on and off, under `solo=` and under `tylko=`.
 *
 * `onlyLine` is deliberately left alone: the line lens only dims marks, it never
 * removes them from `marked`, so the count already survives it. `at` is left
 * alone for the same reason and a stronger one — the hour the resident is
 * reading is the hour the count is about, and lifting it would make the row
 * answer for a different afternoon than the rail beneath it. The radius and the
 * near-now rule stay inside the derivation — no screen may re-derive the
 * geometry, or the two surfaces drift (D-4).
 */
export const unsilenced = (view: ViewState): ViewState => ({ ...view, muted: [], solo: null });

/**
 * Two passes of `buildAxis`, because the two mark sets differ only by the view
 * they are asked about. Measured on a 250-vehicle snapshot the second pass costs
 * about as much as the first and both together are well under a frame, so the
 * honest implementation stands rather than a `buildAxis` threaded with a flag.
 */
export function derive(input: DeriveInput): Derived {
  const { rows, marked, nearby, placed, placeless, beyond } = buildAxis(input);
  const { marked: markedUnsilenced } = buildAxis({ ...input, view: unsilenced(input.view) });
  return {
    rows,
    marked,
    nearby,
    markedUnsilenced,
    placed,
    placeless,
    beyond,
    summary: { vehicles: markedUnsilenced.filter((i) => i.kind === 'vehicle') },
    now: input.now,
  };
}
